import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { SectionContent, getContentResponse } from '../../Services/UserProjectService';
import { useContentVariation, ProjectTreatment } from '../../hooks/useContentVariation';
import AudioTextInput from './AudioTextInput';

interface QuestionSimpleViewerProps {
  content: SectionContent;
  project?: ProjectTreatment;
  onResponseChange?: (isValid: boolean, response: string) => void;
  navigationDirection?: 'forward' | 'backward';
}

const QuestionSimpleViewer: React.FC<QuestionSimpleViewerProps> = ({ content, project, onResponseChange, navigationDirection = 'forward' }) => {
  const [answerText, setAnswerText] = useState('');
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const { getVariationText } = useContentVariation(project);

  const questionText = getVariationText(content);

  // Load saved response if content is completed
  useEffect(() => {
    const loadSavedResponse = async () => {
      if (!content.isCompleted) return;

      // For USER, the content.id IS the projectSectionContentId
      const projectSectionContentId = content.id;

      if (!projectSectionContentId) {
        console.warn('No content ID found for USER content:', content);
        return;
      }

      setIsLoadingResponse(true);
      try {
        const savedResponse = await getContentResponse(projectSectionContentId);
        if (savedResponse && savedResponse.responseContent) {
          setAnswerText(savedResponse.responseContent);
        }
      } catch (error) {
        console.error('Error loading saved response:', error);
      } finally {
        setIsLoadingResponse(false);
      }
    };

    loadSavedResponse();
  }, [content]);

  // Notify parent component when response changes
  useEffect(() => {
    const isValid = answerText.trim().length > 0;
    onResponseChange?.(isValid, answerText.trim());
  }, [answerText, onResponseChange]);

  // Handle audio transcription
  const handleTranscriptChange = (transcribedText: string) => {
    setAnswerText(prev => {
      // Add space before new text if there's existing content
      if (prev && prev.trim()) {
        return `${prev} ${transcribedText}`;
      }
      return transcribedText;
    });
  };

  return (
    <motion.div
      initial={{ x: navigationDirection === 'forward' ? 300 : -300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: navigationDirection === 'forward' ? -300 : 300, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="question-content"
      style={{
        maxWidth: '100%',
        width: '100%',
        padding: '0 2rem'
      }}
    >
      <div className="question-text mb-4" style={{ textAlign: 'left' }}>
        <div
          style={{
            fontSize: '1.1rem', // Fixed size for all questions
            lineHeight: '1.8',
            margin: '0'
          }}
          dangerouslySetInnerHTML={{ __html: questionText }}
        />
      </div>
      <div className="answer-section" style={{ position: 'relative' }}>
        <textarea
          className="form-control"
          style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            minHeight: '200px',
            resize: 'vertical',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            padding: '1rem',
            paddingBottom: '3rem' // Make room for microphone button at bottom
          }}
          placeholder={isLoadingResponse ? "Cargando respuesta guardada..." : "Escribe tu respuesta aquí..."}
          value={answerText}
          onChange={(e) => setAnswerText(e.target.value)}
          disabled={isLoadingResponse}
        />

        {/* Audio Transcription Button */}
        <div style={{
          position: 'absolute',
          bottom: '12px',
          right: '12px',
          zIndex: 10
        }}>
          <AudioTextInput
            onTranscriptChange={handleTranscriptChange}
            disabled={isLoadingResponse}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default QuestionSimpleViewer;